import { accessServiceApiSlice } from "state/services/accessServiceApiSlice";

// Builds a query string from an object, dropping undefined/null/empty values.
const toQueryString = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

// The Arbiter Auth API returns Mongo-style records keyed by `_id` rather than
// `id`. MUI DataGrid (and every id-keyed lookup in this feature) expects `id`,
// so records are normalized once here at the API boundary rather than in every
// consuming component.
const withId = (record) =>
  record && record.id === undefined && record._id !== undefined
    ? { ...record, id: record._id }
    : record;

export const accessServiceApiQueries = accessServiceApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // GET /roles?page=&per_page=&role_name=&id=
    getRoles: builder.query({
      query: ({ page, perPage, roleName, id } = {}) => ({
        url: `/roles${toQueryString({ page, per_page: perPage, role_name: roleName, id })}`,
      }),
      transformResponse: (responseData) => ({
        roles: (responseData?.roles || []).map(withId),
        page: responseData?.page ?? null,
        per_page: responseData?.per_page ?? null,
        total: responseData?.total ?? null,
      }),
      providesTags: (result) =>
        result?.roles
          ? [
              ...result.roles.map((role) => ({ type: "Role", id: role.id })),
              { type: "Role", id: "LIST" },
            ]
          : [{ type: "Role", id: "LIST" }],
    }),

    getRoleById: builder.query({
      query: (roleId) => ({ url: `/roles${toQueryString({ id: roleId })}` }),
      transformResponse: (responseData) => withId(responseData?.roles?.[0]) || null,
      providesTags: (result, error, roleId) => [{ type: "Role", id: roleId }],
    }),

    // GET /role-assignments — all filters are exact-match, combined with AND.
    // page/perPage are optional; omitting both returns every matching record.
    // group_by (role|email) returns a `groups` array instead of a flat
    // `role_assignments` array — see transformResponse below.
    getRoleAssignments: builder.query({
      query: ({
        page,
        perPage,
        oid,
        id,
        userId,
        email,
        orgId,
        roleId,
        isActive,
        isApp,
        groupBy,
        sortBy,
        sortOrder,
      } = {}) => ({
        url: `/role-assignments${toQueryString({
          page,
          per_page: perPage,
          oid,
          id,
          user_id: userId,
          email,
          org_id: orgId,
          role_id: roleId,
          is_active: isActive,
          is_app: isApp,
          group_by: groupBy,
          sort_by: sortBy,
          sort_order: sortOrder,
        })}`,
      }),
      transformResponse: (responseData) => ({
        role_assignments: responseData?.role_assignments
          ? responseData.role_assignments.map(withId)
          : undefined,
        groups: responseData?.groups
          ? responseData.groups.map((group) => ({
              ...group,
              role_assignments: (group.role_assignments || []).map(withId),
            }))
          : undefined,
        page: responseData?.page ?? null,
        per_page: responseData?.per_page ?? null,
        total: responseData?.total ?? null,
      }),
      providesTags: (result) => {
        const allAssignments =
          result?.role_assignments ||
          (result?.groups || []).flatMap((group) => group.role_assignments || []);
        return allAssignments.length
          ? [
              ...allAssignments.map((a) => ({ type: "RoleAssignment", id: a.id })),
              { type: "RoleAssignment", id: "LIST" },
            ]
          : [{ type: "RoleAssignment", id: "LIST" }];
      },
    }),

    getRoleAssignmentById: builder.query({
      query: (id) => ({ url: `/role-assignments${toQueryString({ id })}` }),
      transformResponse: (responseData) => withId(responseData?.role_assignments?.[0]) || null,
      providesTags: (result, error, id) => [{ type: "RoleAssignment", id }],
    }),

    // GET /assigned/user/roles?user_id=|email=|oid= — replaces the old,
    // separate /user/roles and /user/organizations endpoints with a single
    // call returning both the user's global roles and their per-org roles.
    // Response: { oid, email, roles: string[], organizations_roles: [{ role,
    // org_ids: string[] }] }. organizations_roles groups by role rather than
    // repeating a row per org, so it's expanded back into the flat
    // `organization: [{org_id, role}]` shape the rest of the app already
    // expects (one entry per org, e.g. a user who is Leadership in org A and
    // org B gets two entries here, one per org).
    // 404 means the user has no (active) role assignment at all — treated as
    // "no roles/orgs" here rather than an error, since that's a valid, common
    // state.
    getUserAccess: builder.query({
      query: ({ userId, email, oid } = {}) => ({
        url: `/assigned/user/roles${toQueryString({ user_id: userId, email, oid })}`,
      }),
      transformResponse: (responseData) => ({
        email: responseData?.email ?? null,
        roleNames: Array.isArray(responseData?.roles) ? responseData.roles : [],
        organization: (responseData?.organizations_roles || []).flatMap(({ role, org_ids }) =>
          (org_ids || []).map((orgId) => ({ org_id: orgId, role }))
        ),
      }),
      transformErrorResponse: (baseQueryReturnValue) =>
        baseQueryReturnValue?.status === 404
          ? { status: 404, data: { email: null, roleNames: [], organization: [] } }
          : baseQueryReturnValue,
      providesTags: ["Permissions", "EffectiveAccess"],
    }),

    // GET /roles/permissions?category= — the full catalog of every assignable
    // permission string the backend knows about, independent of which roles
    // currently hold them. Replaces deriving the catalog by flattening
    // permissions[] across already-fetched roles (RolesPage's old
    // allPermissions), which could only ever show permissions at least one
    // role already had — a scope no role has been assigned yet was invisible.
    // category filters to one namespace (e.g. "auth"); omitted returns all.
    getPermissionsCatalog: builder.query({
      query: ({ category } = {}) => ({
        url: `/roles/permissions${toQueryString({ category })}`,
      }),
      transformResponse: (responseData) =>
        Array.isArray(responseData?.permissions) ? responseData.permissions : [],
      providesTags: ["Permissions"],
    }),
  }),
});

export const {
  useGetRolesQuery,
  useGetRoleByIdQuery,
  useGetRoleAssignmentsQuery,
  useGetRoleAssignmentByIdQuery,
  useLazyGetRoleAssignmentByIdQuery,
  useGetUserAccessQuery,
  useLazyGetUserAccessQuery,
  useGetPermissionsCatalogQuery,
} = accessServiceApiQueries;
